import { useEffect, useRef } from "react";

const DEFAULT_THRESHOLD_MS = 3 * 60 * 1000;

type UsePresentationAutoCompleteArgs = {
  /** True while the user is in a live (non-preview) presentation. */
  active: boolean;
  /** Called once when accumulated visible time reaches the threshold. */
  onAutoComplete: () => void | Promise<void>;
  thresholdMs?: number;
};

/**
 * Accumulates time spent in Presentation Mode while the tab is visible.
 * - Tab hidden → pause (resume on return)
 * - Presentation ends (`active` false) → clear accumulated time
 * - Does not care about fullscreen state
 */
export function usePresentationAutoComplete({
  active,
  onAutoComplete,
  thresholdMs = DEFAULT_THRESHOLD_MS,
}: UsePresentationAutoCompleteArgs) {
  const accumulatedMsRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const onAutoCompleteRef = useRef(onAutoComplete);
  onAutoCompleteRef.current = onAutoComplete;

  useEffect(() => {
    if (!active) {
      accumulatedMsRef.current = 0;
      lastTickRef.current = null;
      firedRef.current = false;
      return;
    }

    const flush = () => {
      if (lastTickRef.current == null) return;
      accumulatedMsRef.current += Date.now() - lastTickRef.current;
      lastTickRef.current = null;
    };

    const maybeComplete = () => {
      if (firedRef.current) return;
      if (accumulatedMsRef.current < thresholdMs) return;
      firedRef.current = true;
      void onAutoCompleteRef.current();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush();
        return;
      }
      lastTickRef.current = Date.now();
    };

    const intervalId = window.setInterval(() => {
      if (firedRef.current) return;
      if (document.visibilityState !== "visible") {
        lastTickRef.current = null;
        return;
      }
      const now = Date.now();
      if (lastTickRef.current != null) {
        accumulatedMsRef.current += now - lastTickRef.current;
      }
      lastTickRef.current = now;
      maybeComplete();
    }, 1000);

    if (document.visibilityState === "visible") {
      lastTickRef.current = Date.now();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      flush();
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [active, thresholdMs]);
}
